/**
 * Pure grid logic for "Add PCO → Historical Data" — the PM's catch-up entry of
 * a PCO's whole pre-portal billing history (3–14 rows off Josh's PCO Log) as
 * ledger-only submissions.
 *
 * EVERY decision the flow makes that can be checked without a browser lives
 * here: the wire payload, the whole-grid validation pass that must come back
 * clean before ANY row POSTs, the start-number suggestion, and the numeric
 * number comparison. `AddHistoricalPcoFlow.tsx` is then the keyboard, the
 * layout, and the sequential save loop — nothing that needs a truth table.
 *
 * The one rule this file exists to protect: **nothing posts until the whole
 * grid is clean.** A half-saved grid is the failure mode that costs the PM
 * their afternoon (which rows landed? which numbers are now taken?), so the
 * validation pass is a single function over the whole grid, not a per-field
 * check scattered through the component.
 */
import type { AddHistoricalSubmissionRequest } from '@coffman-tm/shared';
import { parseAmountCents } from '../components/pco/BucketRowEditor';

/** Sent-awaiting (`closed`) or approved-to-bill. Never `open` — see the API. */
export type HistoricalRowStatus = 'closed' | 'approved';

/** One row of the grid: one ledger-only submission off the PCO Log. */
export interface HistoricalGridRow {
  /** Stable React key. */
  key: string;
  /** The ".N" — prefixed on screen by the parent PCO number. */
  number: string;
  /** The log's literal worked-date range ("3.21.26 - 4.11.26"). Free text. */
  range: string;
  /** MoneyInput raw string, in dollars. */
  amountDollars: string;
  status: HistoricalRowStatus;
}

/**
 * The MOD / FWA pair shared by every row (client ruling 2026-07-30 #10: one
 * PCO's history carries one pair; the rare exception is fixed afterward from
 * the submission's own inline edit).
 */
export interface SharedReferences {
  mod: string;
  fwa: string;
}

/** What pre-flight needs off each submission already on the PCO. */
export interface ExistingSubmissionLite {
  submission_number: string;
  status: 'open' | 'closed' | 'approved';
}

/** Server cap on `imported_range_label` (400 `invalid_range_label` past it). */
export const RANGE_MAX_LENGTH = 120;

export function newRowKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `hrow-${Math.random().toString(36).slice(2)}`;
}

export function blankHistoricalRow(
  number = '',
  status: HistoricalRowStatus = 'closed',
): HistoricalGridRow {
  return { key: newRowKey(), number, range: '', amountDollars: '', status };
}

/**
 * Mirror of the API's `submissionNumbersMatch` (`submissions/lifecycle.ts`).
 * The DB unique is on the literal text, so '12' and '012' are two rows it
 * happily accepts — and two packages that print as the same number to the GC.
 * The server compares numerically; the grid has to compare the same way or it
 * would wave through a row the POST then 409s.
 */
export function submissionNumbersMatch(a: string, b: string): boolean {
  const x = a.trim();
  const y = b.trim();
  if (/^\d+$/.test(x) && /^\d+$/.test(y)) {
    return Number.parseInt(x, 10) === Number.parseInt(y, 10);
  }
  return x === y;
}

/** Numeric value of a submission number, or 0 when it isn't a plain integer. */
function numericValue(n: string): number {
  return Number.parseInt(n.trim(), 10) || 0;
}

/**
 * The wire body for one row. Deliberately NEVER carries `is_default_target`:
 * the field isn't in the request type and the server hardcodes false, and a
 * ledger total that became the PCO's collecting home would be money the
 * member-rollups-win precedence then discards.
 */
export function historicalSubmissionPayload(
  row: HistoricalGridRow,
  refs: SharedReferences,
): AddHistoricalSubmissionRequest {
  return {
    number: row.number.trim(),
    imported_total_cents: parseAmountCents(row.amountDollars),
    status: row.status,
    imported_range_label: row.range.trim() || null,
    mod_number: refs.mod.trim() || null,
    hoffman_fwa: refs.fwa.trim() || null,
  };
}

/** The number the next row takes when Enter appends one (blank if not numeric). */
export function nextRowNumber(number: string): string {
  const t = number.trim();
  return /^\d+$/.test(t) ? String(Number.parseInt(t, 10) + 1) : '';
}

/**
 * The number an INSERTED row should take: `seed` if nothing already holds it,
 * else the next integer that nothing holds.
 *
 * Enter in the middle of a filled grid is the case this exists for. Seeding a
 * mid-grid insert with "the row above + 1" hands it the number the row BELOW
 * already has, and the duplicate then gets flagged on the untouched
 * pre-existing row (validateGrid blames the later index) — the PM sees an
 * error on a row they didn't touch. Skipping to the first free number means
 * the insert is simply valid; they can retype it if the log says otherwise.
 *
 * A non-integer seed (the row above wasn't numeric) stays blank — there is no
 * sequence to continue.
 */
export function nextFreeRowNumber(
  seed: string,
  rows: HistoricalGridRow[],
  existingNumbers: string[],
): string {
  if (!/^\d+$/.test(seed.trim())) return '';
  const taken = (n: string) =>
    rows.some((r) => r.number.trim() !== '' && submissionNumbersMatch(r.number, n)) ||
    existingNumbers.some((e) => submissionNumbersMatch(e, n));
  let n = Number.parseInt(seed.trim(), 10);
  // Bounded so a pathological set can never spin: a PCO's history is tens of
  // rows, not thousands.
  for (let guard = 0; taken(String(n)) && guard < 1000; guard++) n += 1;
  return String(n);
}

/**
 * The grid once the pre-flight comes back with where this PCO's history
 * should start.
 *
 * The suggestion lands as a real, editable value in the number fields rather
 * than as a sentence above the grid, so it has to be careful about what it
 * overwrites. Three cases:
 *
 *   - `numberEdited` — a row ON SCREEN carries a number the PM typed. The
 *     whole grid is left ALONE, whatever shape it is in and whatever the new
 *     destination suggests. Those numbers came off the PCO Log and are the
 *     ones the GC knows; quietly rewriting them is the one thing this must
 *     never do. If they clash with the PCO now selected, `validateGrid` and
 *     the open-package banner say so out loud, which is the honest outcome.
 *   - typed rows, no hand-typed number — every number in the grid was put
 *     there by this function or by `nextFreeRowNumber`, so the whole run is
 *     re-seated onto the new start (14, 15, 16…) and every range and amount
 *     survives. This covers both the fetch racing the first keystroke and a
 *     PM who typed several rows before noticing the wrong PCO was selected.
 *   - anything else — replaced with one blank row at the start number: the
 *     grid held nothing worth keeping, or (`landed`) rows in it have already
 *     been saved onto a PCO and cannot follow the PM to a different one.
 *
 * `numberEdited` is a fact about the CURRENT grid, not about the session —
 * the caller asks whether any row still on screen was hand-numbered. That is
 * what makes the claim self-clearing: a replacement drops it, because the
 * fresh row's key was never marked.
 */
export function applySuggestedStart(
  rows: HistoricalGridRow[],
  start: string,
  opts: { numberEdited: boolean; landed: boolean },
): HistoricalGridRow[] {
  if (opts.numberEdited) return rows;
  const base = Number.parseInt(start.trim(), 10);
  if (!opts.landed && rows.some(rowHasContent) && Number.isFinite(base)) {
    return rows.map((r, i) => ({ ...r, number: String(base + i) }));
  }
  return [blankHistoricalRow(start)];
}

/**
 * Where this PCO's history should start numbering.
 *
 *   nothing on the PCO           → 1
 *   anything non-open present    → MAX(numeric across ALL) + 1  (history appends)
 *   only OPEN packages present   → 1
 *
 * The third case is the onboarding collision: a foreman PDF auto-opened `.1`
 * before the PM entered the history, and the history's real numbers start at 1.
 * We keep the log's numbers and offer to renumber the open package instead
 * (client ruling 2026-07-30 #9) — only OPEN packages are ever renumbered.
 */
export function suggestStartNumber(existing: ExistingSubmissionLite[]): string {
  if (existing.length === 0) return '1';
  const settled = existing.filter((s) => s.status !== 'open');
  if (settled.length === 0) return '1';
  const max = existing.reduce((m, s) => Math.max(m, numericValue(s.submission_number)), 0);
  return String(max + 1);
}

/**
 * The OPEN submission whose number the grid is about to take, or null. Only
 * open packages are candidates — a closed/approved one has been sent under
 * that number and can never be renumbered.
 */
export function openRenumberCandidate(
  existing: ExistingSubmissionLite[],
  rows: HistoricalGridRow[],
): ExistingSubmissionLite | null {
  return (
    existing.find(
      (s) =>
        s.status === 'open' &&
        rows.some((r) => r.number.trim() !== '' && submissionNumbersMatch(r.number, s.submission_number)),
    ) ?? null
  );
}

/** The number the open package would be renumbered TO — past everything else. */
export function renumberTarget(
  existing: ExistingSubmissionLite[],
  rows: HistoricalGridRow[],
): string {
  const fromExisting = existing.reduce(
    (m, s) => Math.max(m, numericValue(s.submission_number)),
    0,
  );
  const fromGrid = rows.reduce((m, r) => Math.max(m, numericValue(r.number)), 0);
  return String(Math.max(fromExisting, fromGrid) + 1);
}

export type HistoricalRowField = 'number' | 'amount' | 'range';

export type HistoricalRowErrors = Partial<Record<HistoricalRowField, string>>;

/** Per-row errors keyed by `row.key`. Empty object = the whole grid is clean. */
export type HistoricalGridErrors = Record<string, HistoricalRowErrors>;

/**
 * The whole-grid pass. Runs before a single POST goes out; a non-empty result
 * means nothing is sent.
 *
 * `existingNumbers` is EVERY submission number already on the PCO, any status
 * — the same set the server's clash check reads.
 */
export function validateGrid(
  rows: HistoricalGridRow[],
  existingNumbers: string[],
): HistoricalGridErrors {
  const out: HistoricalGridErrors = {};
  const put = (key: string, field: HistoricalRowField, message: string) => {
    const row = out[key] ?? {};
    if (row[field] === undefined) row[field] = message;
    out[key] = row;
  };

  rows.forEach((row, i) => {
    const number = row.number.trim();
    if (number === '') {
      put(row.key, 'number', 'Enter a number.');
    } else if (existingNumbers.some((n) => submissionNumbersMatch(n, number))) {
      // The taken list lives HERE, in the error, rather than in a standing
      // line above the grid: it only matters at the moment a row collides.
      // It is built per row so the elision can never drop the number the
      // sentence is about.
      put(
        row.key,
        'number',
        `Already used on this PCO — taken: ${takenNumbersLabel(existingNumbers, number)}.`,
      );
    } else if (
      rows.some((other, j) => j < i && submissionNumbersMatch(other.number, number))
    ) {
      put(row.key, 'number', 'Repeats a row above.');
    }

    // Client ruling 2026-08-06: hand-entered billed history is never negative.
    // A deduct on the billing side is done by reopening the week and removing
    // hours — not by keying a negative bill. (Funding-side deducts are a
    // different concept and DO go negative: 062/063.)
    if (parseAmountCents(row.amountDollars) <= 0) {
      put(row.key, 'amount', 'Enter an amount over $0.');
    }

    if (row.range.trim().length > RANGE_MAX_LENGTH) {
      put(row.key, 'range', `Keep this under ${RANGE_MAX_LENGTH} characters.`);
    }
  });

  return out;
}

export function gridHasErrors(errors: HistoricalGridErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** A row the PM has actually started filling in — the dirty-close unit. */
export function rowHasContent(row: HistoricalGridRow): boolean {
  return row.range.trim() !== '' || row.amountDollars.trim() !== '';
}

/**
 * Rows that repeat another row's amount AND worked range. Almost always a
 * double-entry off the log — but "we billed the same amount for the same span
 * twice" is a real thing on this project, so it is a MUTED NOTE and never
 * blocks the save.
 */
export function duplicateContentKeys(rows: HistoricalGridRow[]): Set<string> {
  const seen = new Map<string, string[]>();
  for (const r of rows) {
    const cents = parseAmountCents(r.amountDollars);
    if (cents <= 0) continue;
    const sig = `${cents}|${r.range.trim().toLowerCase()}`;
    seen.set(sig, [...(seen.get(sig) ?? []), r.key]);
  }
  const out = new Set<string>();
  for (const keys of seen.values()) {
    if (keys.length > 1) for (const k of keys) out.add(k);
  }
  return out;
}

/** Live footer total across every row with a parseable amount. */
export function gridTotalCents(rows: HistoricalGridRow[]): number {
  return rows.reduce((a, r) => a + parseAmountCents(r.amountDollars), 0);
}

/** How many groups the taken list prints before it gives up and elides. */
const TAKEN_LABEL_MAX_GROUPS = 6;

/**
 * The numbers already on the PCO, on ONE line — "1–13", "1, 2, 10",
 * "1–3, 5, 7–9". It rides inside a row's number error, so a fourteen-package
 * history has to read at a glance instead of listing fourteen integers.
 *
 * Duplicates are dropped the way the rest of this module compares numbers —
 * NUMERICALLY, so a PCO carrying both '12' and '012' (the DB unique is on the
 * literal text; `submissionNumbersMatch` is not) prints one of them, the
 * first spelling seen. Singletons and pairs keep that spelling, because the
 * PM is matching them against the log; a RANGE prints plain integers, since
 * "10–012" is a numeric statement wearing someone else's leading zero.
 *
 * `keep` is the number the error is actually about. When the list is long
 * enough to elide, its group is guaranteed to survive — a sentence naming
 * what's taken must not drop its own subject.
 */
export function takenNumbersLabel(numbers: string[], keep?: string): string {
  const isInt = (n: string) => /^\d+$/.test(n);
  const seenText = new Set<string>();
  const seenValue = new Set<number>();
  const sorted: string[] = [];
  for (const raw of numbers) {
    const n = raw.trim();
    if (n === '') continue;
    if (isInt(n)) {
      const v = numericValue(n);
      if (seenValue.has(v)) continue;
      seenValue.add(v);
    } else {
      if (seenText.has(n)) continue;
      seenText.add(n);
    }
    sorted.push(n);
  }
  sorted.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const runs: string[][] = [];
  for (const n of sorted) {
    const run = runs[runs.length - 1];
    const last = run?.[run.length - 1];
    if (run && last !== undefined && isInt(n) && isInt(last) && numericValue(n) === numericValue(last) + 1) {
      run.push(n);
    } else {
      runs.push([n]);
    }
  }

  const groups = runs.map((r) =>
    r.length <= 2 ? r.join(', ') : `${numericValue(r[0]!)}–${numericValue(r[r.length - 1]!)}`,
  );
  if (groups.length <= TAKEN_LABEL_MAX_GROUPS) return groups.join(', ');

  const keepAt = keep === undefined ? -1 : runs.findIndex((r) => r.some((n) => submissionNumbersMatch(n, keep)));
  if (keepAt < TAKEN_LABEL_MAX_GROUPS) {
    return `${groups.slice(0, TAKEN_LABEL_MAX_GROUPS).join(', ')}, …`;
  }
  // The elided middle sits BEFORE the kept group so the line still reads in
  // order: what the PCO starts with, a gap, then the number in question.
  return `${groups.slice(0, TAKEN_LABEL_MAX_GROUPS - 1).join(', ')}, …, ${groups[keepAt]}`;
}

/** The submission MOD/FWA prefill reads from — highest number, any status. */
export function referenceSourceOf<T extends { submission_number: string }>(
  existing: T[],
): T | null {
  return existing.reduce<T | null>(
    (best, s) =>
      best === null || numericValue(s.submission_number) > numericValue(best.submission_number)
        ? s
        : best,
    null,
  );
}
